/**
 * Layer contracts (plan §4.1, owner ui-layers). Every floating surface of the app is a *layer* in one
 * stack: DOM order inside `#dr-layers` is stacking order, Escape closes only the top layer, and route
 * changes, dirty guards, `inert` and focus return are decided by the stack, never by a component's own
 * `document` listener.
 */

/** What a layer is. The kind decides the dismiss policy (see `DISMISS_POLICY`). */
export type LayerKind =
  | 'tooltip'
  | 'popover'
  | 'menu'
  | 'combobox'
  | 'select'
  | 'sheet'
  | 'dialog'
  | 'alert'
  | 'drawer'
  | 'palette'
  | 'panel'
  | 'inspector-flyout'
  | 'presentation'
  | 'drag-overlay'
  | 'inline';

/** Why a layer is asked to close. Passed to `onClose`/`onOpenChange` callbacks of the primitives. */
export type LayerCloseReason =
  /** Escape key routed to the top layer. */
  | 'escape'
  /** Pointer press outside the layer (and outside every layer above it). */
  | 'outside'
  /** The close button in the layer chrome (×). */
  | 'close-button'
  /** Swipe or drag-to-dismiss of a sheet. */
  | 'swipe'
  /** The route changed (pathname change or history traversal). */
  | 'navigate'
  /** The anchor of an anchored layer scrolled out of view. Inline editors commit on this reason. */
  | 'anchor-hidden'
  /** A modal layer opened above a transient layer that is not its ancestor. */
  | 'superseded'
  /** A modal child that suspended this layer closed. */
  | 'child-closed'
  /** Focus moved out of a non-modal layer. */
  | 'focus-out'
  /** An item was chosen (menus, selects, `closeOnSelect` popovers). */
  | 'select'
  /** Closed by code (`close()` render prop, `closeLayer`, controlled `open={false}`). */
  | 'programmatic';

/** Reasons that consult the dirty guard ("Discard changes?") before closing. */
export const GUARDED_REASONS: ReadonlySet<LayerCloseReason> = new Set([
  'escape',
  'outside',
  'close-button',
  'swipe',
  'navigate',
  'superseded',
]);

/** Transient kinds: closed (or suspended, when they are an ancestor) when a modal layer opens. */
export const TRANSIENT_KINDS: ReadonlySet<LayerKind> = new Set([
  'tooltip',
  'popover',
  'menu',
  'combobox',
  'select',
  'sheet',
  'inline',
]);

/**
 * The dismiss table of `interaction-systems.md` §3.2, enforced by the stack.
 * `route`: what a route change does (`close`, `guard` = ask when dirty, `keep` = stays).
 */
export const DISMISS_POLICY: Readonly<
  Record<LayerKind, { escape: boolean; outside: boolean; route: 'close' | 'guard' | 'keep'; modal: boolean }>
> = {
  tooltip: { escape: true, outside: true, route: 'close', modal: false },
  popover: { escape: true, outside: true, route: 'close', modal: false },
  menu: { escape: true, outside: true, route: 'close', modal: false },
  combobox: { escape: true, outside: true, route: 'close', modal: false },
  select: { escape: true, outside: true, route: 'close', modal: false },
  sheet: { escape: true, outside: true, route: 'close', modal: false },
  inline: { escape: true, outside: true, route: 'close', modal: false },
  dialog: { escape: true, outside: true, route: 'guard', modal: true },
  alert: { escape: true, outside: false, route: 'guard', modal: true },
  drawer: { escape: true, outside: true, route: 'guard', modal: true },
  palette: { escape: true, outside: true, route: 'close', modal: true },
  panel: { escape: true, outside: false, route: 'keep', modal: false },
  'inspector-flyout': { escape: true, outside: true, route: 'close', modal: false },
  presentation: { escape: true, outside: false, route: 'close', modal: true },
  'drag-overlay': { escape: true, outside: false, route: 'close', modal: false },
};

/** Options of `useLayer`. */
export type LayerOptions = {
  kind: LayerKind;
  /** Whether the layer is open (registered). */
  open: boolean;
  /** Called when the stack decided the layer closes (after the dirty guard). */
  onClose: (reason: LayerCloseReason) => void;
  /** Overrides the kind's default (`DISMISS_POLICY[kind].modal`). Modal layers make the rest `inert`. */
  modal?: boolean;
  /** Pinned layers survive route changes (Copilot panel). */
  pinned?: boolean;
  /** Unsaved input: Escape, backdrop, swipe and route change ask "Discard changes?" first. */
  dirty?: boolean;
  /**
   * `self`: a Base UI primitive handles Escape itself and asks the stack (`handleDismiss`).
   * `router` (default): the stack's single Escape router closes the layer when it is on top.
   */
  escape?: 'self' | 'router';
  /**
   * For `router` layers: close on pointer presses outside `getElements()`
   * (default `DISMISS_POLICY[kind].outside`: true except for panels, alerts, presentations).
   */
  dismissOnOutsidePress?: boolean;
  /**
   * For `router` layers on top: return true to consume Escape instead of closing (a panel that
   * first stops its streaming run). Called before the layer would close.
   */
  onEscape?: (event: KeyboardEvent) => boolean;
  /** Elements that count as "inside" the layer (popup, anchor) for outside presses and inert. */
  getElements?: () => Array<Element | null | undefined>;
  /** Shows the layer's own inline "Discard changes?" confirm; resolves true to discard. */
  confirmDiscard?: () => Promise<boolean>;
  /** Accessible name used by the generic discard confirm when the layer has no inline one. */
  label?: string;
};

/** A registered layer (read-only view for hosts and tests). */
export type LayerEntry = {
  readonly id: string;
  readonly kind: LayerKind;
  readonly modal: boolean;
  readonly pinned: boolean;
  readonly parentId: string | null;
  readonly order: number;
  readonly suspended: boolean;
  readonly dirty: boolean;
  readonly label: string | undefined;
};

/** Navigation events LayerRoot listens to (router-agnostic; `__root.tsx` adapts TanStack Router). */
export type LayerNavigationEvent = {
  /** `push`/`replace` for in-app navigation, `traverse` for browser back/forward. */
  kind: 'push' | 'replace' | 'traverse';
  pathChanged: boolean;
};

export type LayerNavigationSource = {
  subscribe: (listener: (event: LayerNavigationEvent) => void) => () => void;
};
